<template>
  <div class="home-page" :class="{ 'page-ready': pageReady }">
    <Header />

    <!-- Hero Section -->
    <section class="hero-section">
      <div class="hero-overlay"></div>
      <div class="container hero-content animate-in">
        <h1 class="hero-title">Memorial Parque Maceió</h1>
        <p class="hero-subtitle">Sua loja online de serviços funerários, planos de assistência, cremação e floricultura. Compre com tranquilidade e segurança.</p>
        <div class="hero-actions">
          <router-link to="/catalogo" class="btn-primary">
            <svg class="btn-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/>
              <line x1="3" y1="6" x2="21" y2="6"/>
              <path d="M16 10a4 4 0 0 1-8 0"/>
            </svg>
            Ver Catálogo Completo
          </router-link>
          <router-link to="/floricultura" class="btn-secondary">
            <img src="../assets/memorial-flores.png" alt="" class="btn-flor-icon" onerror="this.style.display='none'" />
            Floricultura
          </router-link>
        </div>
        <router-link to="/catalogo" class="hero-explore">Explorar produtos →</router-link>
      </div>
    </section>

    <!-- Destaques - Carousel -->
    <section v-if="produtosDestaques.length > 0" class="featured-section">
      <div class="container animate-from-left">
        <div class="featured-header">
          <span class="featured-badge">
            <svg class="featured-star-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
              <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
            </svg>
            Destaques & Promoções
          </span>
          <div class="featured-nav">
            <button type="button" @click="prevDestaque" aria-label="Anterior">‹</button>
            <button type="button" @click="nextDestaque" aria-label="Próximo">›</button>
          </div>
        </div>
        <router-link
          v-if="destaqueAtual"
          :to="`/produto/${destaqueAtual.id}`"
          class="featured-card"
        >
          <div class="featured-image">
            <img
              v-if="imagemDestaque"
              :src="imagemDestaque"
              :alt="destaqueAtual.nome"
            />
            <img
              v-else-if="imagemPadraoDestaque"
              :src="imagemPadraoDestaque"
              :alt="destaqueAtual.nome"
            />
            <div v-else class="featured-placeholder">Destaque</div>
            <span class="featured-label">Destaque</span>
          </div>
          <div class="featured-info">
            <h2 class="featured-title">{{ destaqueAtual.nome }}</h2>
            <p class="featured-desc">{{ destaqueAtual.descricao }}</p>
            <span class="featured-price">R$ {{ formatPrice(destaqueAtual.valor) }}</span>
            <span class="featured-link">Ver detalhes →</span>
          </div>
        </router-link>
        <div class="featured-dots">
          <button
            v-for="(_, i) in produtosDestaques"
            :key="i"
            type="button"
            :class="['dot', { active: destaqueIndex === i }]"
            :aria-label="`Slide ${i + 1}`"
            @click="destaqueIndex = i"
          />
        </div>
      </div>
    </section>

    <!-- Serviços em Destaque - ProductCards -->
    <section v-if="produtosServicos.length > 0" class="services-section">
      <div class="container animate-from-right">
        <div class="section-header">
          <span class="section-badge">Serviços em Destaque</span>
          <h2 class="section-title">Soluções completas para sua família</h2>
          <p class="section-subtitle">Jazigos, planos funerários, cremação e urnas cinerárias. Contrate online com segurança.</p>
        </div>
        <div class="products-grid products-grid-2">
          <ProductCard
            v-for="produto in produtosServicos"
            :key="produto.id"
            :produto="produto"
            :categorias="categorias"
          />
        </div>
        <div class="section-link">
          <router-link to="/catalogo">Ver catálogo completo →</router-link>
        </div>
      </div>
    </section>

    <!-- Floricultura - ProductCards -->
    <section v-if="produtosFloricultura.length > 0" class="floricultura-section">
      <div class="container animate-from-left">
        <div class="section-header floricultura-header">
          <img src="../assets/memorial-flores.png" alt="Memorial Flores" class="floricultura-logo" onerror="this.style.display='none'" />
          <h2 class="section-title">Nossa Floricultura</h2>
          <p class="section-subtitle">Flores cultivadas com carinho para homenagear quem você ama. Compre online com entrega.</p>
        </div>
        <div class="products-grid products-grid-3">
          <ProductCard
            v-for="produto in produtosFloricultura"
            :key="produto.id"
            :produto="produto"
            :categorias="categorias"
          />
        </div>
        <div class="section-link">
          <router-link to="/floricultura">Ver os produtos da floricultura →</router-link>
        </div>
      </div>
    </section>

    <!-- Cremação -->
    <section class="cremacao-section">
      <div class="container cremacao-content animate-cremacao">
        <div class="cremacao-text">
          <span class="section-badge">Cremação</span>
          <h2 class="section-title">Um adeus com respeito e serenidade</h2>
          <p class="cremacao-desc">Nosso serviço de cremação é conduzido em instalações modernas, com ambiente acolhedor e profissionais capacitados.</p>
          <ul class="cremacao-features">
            <li v-for="feat in cremacaoFeatures" :key="feat.titulo" class="cremacao-feature">
              <div class="feat-icon" v-html="feat.iconSvg"></div>
              <div>
                <h4>{{ feat.titulo }}</h4>
                <p>{{ feat.descricao }}</p>
              </div>
            </li>
          </ul>
          <router-link to="/catalogo?categoria=cremacao" class="btn-cta btn-center">
            <svg class="btn-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/>
              <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/>
            </svg>
            Ver Opções de Cremação
          </router-link>
        </div>
        <div class="cremacao-images">
          <div class="cremacao-img">
            <img src="/img/cremacao.jpg" alt="Espaço de cremação" onerror="this.src='https://images.unsplash.com/photo-1544716278-ca5e3f4abd8c?w=600'" />
          </div>
          <div class="cremacao-img">
            <img src="/img/urna.jpg" alt="Urna cinerária" onerror="this.src='https://images.unsplash.com/photo-1516455590571-18256e5bb9ff?w=600'" />
          </div>
        </div>
      </div>
    </section>

    <!-- Contato -->
    <section class="contact-section">
      <div class="container animate-in">
        <div class="section-header light">
          <h2 class="section-title">Entre em Contato</h2>
          <p class="section-subtitle">Estamos à disposição para atendê-lo com todo cuidado e atenção que você merece.</p>
        </div>
        <div class="contact-grid">
          <a
            v-for="item in contactItems"
            :key="item.titulo"
            :href="item.href || '#'"
            :target="item.href ? '_blank' : undefined"
            :rel="item.href ? 'noopener noreferrer' : undefined"
            :class="['contact-card', { 'no-link': !item.href }]"
            @click="!item.href && $event.preventDefault()"
          >
            <span class="contact-icon" v-html="item.iconSvg"></span>
            <h4>{{ item.titulo }}</h4>
            <p>{{ item.info }}</p>
          </a>
        </div>
      </div>
    </section>

    <Footer />
  </div>
</template>

<script>
import Header from '../components/Header.vue'
import Footer from '../components/Footer.vue'
import ProductCard from '../components/ProductCard.vue'
import produtoService from '../services/produtoService'
import categoriaService from '../services/categoriaService'

const FLORICULTURA_KEYWORDS = ['coroa', 'flor', 'buque', 'buquê', 'vaso', 'arranjo']

export default {
  name: 'Home',
  components: {
    Header,
    Footer,
    ProductCard
  },
  data() {
    return {
      pageReady: false,
      produtos: [],
      categorias: [],
      carregando: false,
      erro: null,
      destaqueIndex: 0,
      destaqueTimer: null,
      produtosDestaques: [],
      cremacaoFeatures: [
        { titulo: 'Cerimônia Personalizada', descricao: 'Possibilidade de realizar cerimônias de despedida respeitosas e personalizadas.', iconSvg: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>' },
        { titulo: 'Segurança e Dignidade', descricao: 'Processo realizado com máxima segurança e respeito aos padrões regulatórios.', iconSvg: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>' },
        { titulo: 'Urnas Cinerárias', descricao: 'Diversas opções de urnas em materiais nobres para guardar as cinzas com dignidade.', iconSvg: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z"/></svg>' }
      ],
      contactItems: [
        { titulo: 'Endereço', info: 'Av. Durval de Góes Monteiro, 9000 - Tabuleiro do Martins, Maceió - AL', iconSvg: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>', href: 'https://maps.google.com/?q=Av+Durval+de+Goes+Monteiro+Maceio+AL' },
        { titulo: 'Telefone', info: '(82) 3334-5000', iconSvg: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/></svg>', href: 'tel:+558233345000' },
        { titulo: 'WhatsApp', info: '(82) 99122-9510', iconSvg: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/></svg>', href: 'https://wa.me/5582991229510' },
        { titulo: 'Atendimento', info: '24 horas, todos os dias', iconSvg: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>', href: null }
      ]
    }
  },
  computed: {
    destaqueAtual() {
      return this.produtosDestaques[this.destaqueIndex] || null
    },
    imagemDestaque() {
      if (!this.destaqueAtual) return null
      const img = this.destaqueAtual.imagem
      if (img) {
        if (img.startsWith('http') || img.startsWith('data:')) {
          return img
        }
        // Nome de arquivo vindo da API
        if (typeof img === 'string' && img.length < 200 && img.indexOf('.') !== -1) {
          return `https://api.grupoparque.com.br/produtos/${img}`
        }
        return `data:image/jpeg;base64,${img}`
      }
      return null
    },
    imagemPadraoDestaque() {
      if (!this.destaqueAtual) return null
      return this.getImagemPadrao(this.destaqueAtual)
    },
    produtosServicos() {
      return this.produtos.filter(p => !this.isFloricultura(p)).slice(0, 6)
    },
    produtosFloricultura() {
      return this.produtos.filter(p => this.isFloricultura(p)).slice(0, 6)
    }
  },
  mounted() {
    this.carregarDados()
    this.iniciarCarousel()
    requestAnimationFrame(() => {
      this.pageReady = true
    })
  },
  beforeDestroy() {
    if (this.destaqueTimer) clearInterval(this.destaqueTimer)
  },
  methods: {
    async carregarDados() {
      this.carregando = true
      this.erro = null
      try {
        this.categorias = await categoriaService.listarCategorias()
        this.produtos = await produtoService.listarProdutos()
        this.produtosDestaques = await produtoService.listarDestaques()
      } catch (err) {
        console.error('Erro ao carregar dados:', err)
        this.erro = 'Erro ao carregar produtos. Tente novamente.'
      } finally {
        this.carregando = false
      }
    },
    isFloricultura(produto) {
      const cat = this.categorias.find(c => c.id === produto.categoriaId)
      if (!cat || !cat.descricao) return false
      const desc = (cat.descricao || '').toLowerCase()
      return FLORICULTURA_KEYWORDS.some(k => desc.includes(k))
    },
    formatPrice(val) {
      return new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(val || 0)
    },
    getImagemPadrao(produto) {
      const categoria = this.categorias.find(c => c.id === produto.categoriaId)
      const nomeCategoria = categoria ? categoria.descricao.toLowerCase() : ''
      const nomeProduto = produto.nome ? produto.nome.toLowerCase() : ''
      const mapeamento = {
        'coroa': 'coroa.jpg', 'coroas': 'coroa.jpg', 'urna': 'urna.jpg', 'urnas': 'urna.jpg',
        'cremação': 'cremacao.jpg', 'cremacao': 'cremacao.jpg', 'cremações': 'cremacao.jpg',
        'jazigo': 'jazigo.jpg', 'jazigos': 'jazigo.jpg', 'plano funerário': 'plano.jpg',
        'planos funerários': 'plano.jpg', 'planos funerarios': 'plano.jpg', 'buque': 'buque.jpg',
        'buquê': 'buque.jpg', 'buques': 'buque.jpg', 'buquês': 'buque.jpg', 'vaso': 'vaso.jpg',
        'vasos': 'vaso.jpg', 'arranjo': 'arranjo.jpg', 'arranjos': 'arranjo.jpg',
        'arranjo floral': 'arranjo.jpg', 'arranjos florais': 'arranjo.jpg'
      }
      for (const [key, img] of Object.entries(mapeamento)) {
        if (nomeCategoria.includes(key)) return `/img/${img}`
      }
      for (const [key, img] of Object.entries(mapeamento)) {
        if (nomeProduto.includes(key)) return `/img/${img}`
      }
      return null
    },
    prevDestaque() {
      this.destaqueIndex = (this.destaqueIndex - 1 + this.produtosDestaques.length) % this.produtosDestaques.length
      this.resetCarousel()
    },
    nextDestaque() {
      this.destaqueIndex = (this.destaqueIndex + 1) % this.produtosDestaques.length
      this.resetCarousel()
    },
    iniciarCarousel() {
      this.destaqueTimer = setInterval(() => {
        if (this.produtosDestaques.length > 0) {
          this.destaqueIndex = (this.destaqueIndex + 1) % this.produtosDestaques.length
        }
      }, 5000)
    },
    resetCarousel() {
      if (this.destaqueTimer) clearInterval(this.destaqueTimer)
      this.iniciarCarousel()
    }
  }
}
</script>

<style scoped>
/* Variáveis do protótipo */
.home-page {
  --primary: hsl(138, 45%, 37%);
  --primary-dark: hsl(138, 50%, 25%);
  --font-display: 'Playfair Display', serif;
  --font-body: 'Source Sans 3', sans-serif;
  min-height: 100vh;
  background: #ffffff;
  color: #1f2937;
  font-family: var(--font-body);
}

/* Inicialização - animações de entrada */
@keyframes slideInLeft {
  from {
    opacity: 0;
    transform: translateX(-40px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes slideInRight {
  from {
    opacity: 0;
    transform: translateX(40px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(24px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes cremacaoIn {
  from {
    opacity: 0;
    transform: translateX(-24px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

.home-page.page-ready .hero-section .animate-in {
  animation: fadeInUp 0.6s ease-out 0.1s both;
}

.home-page.page-ready .animate-from-left {
  animation: slideInLeft 0.6s ease-out both;
}

.home-page.page-ready .animate-from-right {
  animation: slideInRight 0.6s ease-out both;
}

.home-page.page-ready .animate-cremacao {
  animation: cremacaoIn 0.6s ease-out both;
}

.home-page.page-ready .contact-section .animate-in {
  animation: fadeInUp 0.6s ease-out 0.55s both;
}

.home-page.page-ready .featured-section .animate-from-left { animation-delay: 0.15s; animation-fill-mode: both; }
.home-page.page-ready .services-section .animate-from-right { animation-delay: 0.25s; animation-fill-mode: both; }
.home-page.page-ready .floricultura-section .animate-from-left { animation-delay: 0.35s; animation-fill-mode: both; }
.home-page.page-ready .cremacao-section .animate-cremacao { animation-delay: 0.45s; animation-fill-mode: both; }

.container {
  max-width: 1320px;
  margin: 0 auto;
  padding: 0 40px;
}

h1, h2, h3, h4, h5, h6 {
  font-family: var(--font-display);
}

section {
  padding: 40px 0;
}

/* Hero */
.hero-section {
  position: relative;
  min-height: 70vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: url('../assets/BannerMemorial.png'), linear-gradient(135deg, #2d5a3d 0%, #1e3d2a 100%);
  background-size: cover, auto;
  background-position: center;
  background-repeat: no-repeat;
  color: white;
  overflow: hidden;
}

.hero-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(to bottom, hsla(138, 50%, 25%, 0.6) 0%, hsla(138, 50%, 25%, 0.4) 50%, hsla(138, 50%, 25%, 0.7) 100%);
  z-index: 1;
}

.hero-content {
  position: relative;
  z-index: 2;
  text-align: center;
  max-width: 48rem;
}

.hero-title {
  font-size: clamp(2.5rem, 5vw, 4.5rem);
  font-weight: 700;
  margin-bottom: 24px;
  line-height: 1.2;
}

.hero-subtitle {
  font-size: 1.125rem;
  opacity: 0.9;
  margin-bottom: 40px;
  line-height: 1.6;
}

.hero-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  justify-content: center;
  margin-bottom: 32px;
}

.btn-primary,
.btn-secondary {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.btn-primary {
  background: var(--primary);
  color: white;
  padding: 16px 32px;
  border-radius: 8px;
  font-weight: 600;
  text-decoration: none;
  transition: opacity 0.2s;
}

.btn-primary:hover {
  opacity: 0.9;
}

.btn-secondary {
  background: rgba(255, 255, 255, 0.15);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.3);
  padding: 16px 32px;
  border-radius: 8px;
  font-weight: 600;
  text-decoration: none;
  transition: background 0.2s;
}

.btn-secondary:hover {
  background: rgba(255, 255, 255, 0.25);
}

.btn-icon {
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  align-self: center;
}

.btn-flor-icon {
  height: 22px;
  width: auto;
  flex-shrink: 0;
  object-fit: contain;
}

.hero-explore {
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.875rem;
  text-decoration: none;
}

.hero-explore:hover {
  color: white;
}

/* Featured */
.featured-section {
  background: #fff;
  border-bottom: 1px solid #e5e7eb;
  padding: 48px 0;
}

.featured-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 32px;
}

.featured-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 0.875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #d4a01e;
}

.featured-star-icon {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
}

.featured-nav {
  display: flex;
  gap: 8px;
}

.featured-nav button {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: 1px solid #e5e7eb;
  background: #fff;
  cursor: pointer;
  font-size: 1.25rem;
  line-height: 1;
  transition: background 0.2s;
}

.featured-nav button:hover {
  background: #f3f4f6;
}

.featured-card {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  align-items: center;
  text-decoration: none;
  color: inherit;
}

.featured-image {
  position: relative;
  border-radius: 16px;
  aspect-ratio: 16/10;
  overflow: hidden;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.featured-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.featured-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
}

.featured-label {
  position: absolute;
  top: 16px;
  left: 16px;
  background: #d4a01e;
  color: #1f2937;
  font-size: 0.75rem;
  font-weight: 700;
  padding: 6px 12px;
  border-radius: 9999px;
}

.featured-info {
  text-align: left;
}

.featured-title {
  font-size: 1.75rem;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 12px;
}

.featured-desc {
  color: #6b7280;
  margin-bottom: 16px;
  line-height: 1.6;
}

.featured-price {
  display: block;
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--primary);
  margin-bottom: 16px;
}

.featured-link {
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--primary);
}

.featured-dots {
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-top: 32px;
}

.featured-dots .dot {
  width: 8px;
  height: 8px;
  border-radius: 9999px;
  border: none;
  background: #e5e7eb;
  cursor: pointer;
  transition: all 0.3s;
}

.featured-dots .dot.active {
  width: 32px;
  background: var(--primary);
}

/* Section common */
.section-header {
  text-align: center;
  max-width: 42rem;
  margin: 0 auto 64px;
}

.section-header.light .section-title,
.section-header.light .section-subtitle {
  color: white;
}

.section-header.light .section-subtitle {
  opacity: 0.85;
}

.section-badge {
  display: block;
  font-size: 0.875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: var(--primary);
  margin-bottom: 12px;
}

.section-title {
  font-size: clamp(1.875rem, 4vw, 3rem);
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 16px;
}

.section-subtitle {
  font-size: 1.125rem;
  color: #6b7280;
  line-height: 1.6;
}

.section-link {
  text-align: center;
  margin-top: 40px;
}

.section-link a {
  color: var(--primary);
  font-weight: 600;
  text-decoration: none;
}

.section-link a:hover {
  text-decoration: underline;
}

.btn-cta {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: var(--primary);
  color: white !important;
  padding: 14px 32px;
  border-radius: 8px;
  text-decoration: none !important;
}

.btn-cta .btn-icon {
  flex-shrink: 0;
}

.btn-center {
  margin: 0 auto;
}

.btn-cta:hover {
  opacity: 0.9;
  text-decoration: none !important;
}


/* Products grid */
.products-grid {
  display: grid;
  gap: 32px;
}

.products-grid-2 {
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
}

@media (min-width: 768px) {
  .products-grid-2 {
    grid-template-columns: repeat(2, 1fr);
  }
}

.products-grid-3 {
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
}

@media (min-width: 768px) {
  .products-grid-3 {
    grid-template-columns: repeat(3, 1fr);
  }
}

/* Services */
.services-section {
  background: linear-gradient(180deg, #faf8f5, #f5f5f5);
}

/* Floricultura */
.floricultura-section {
  padding-top: 0;
  background: #eef0f0;
  border-top: 1px solid rgba(0, 0, 0, 0.06);
}

.floricultura-header {
  margin-bottom: 48px;
}

.floricultura-logo {
  height: 2 00px;
  width: auto;
  max-width: 100%;
  display: block;
  margin: 0 auto 0;
}

/* Cremação */
.cremacao-section {
  background: #fff;
}

.cremacao-content {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 64px;
  align-items: center;
}

.cremacao-text .section-badge {
  display: inline-block;
  margin-bottom: 12px;
}

.cremacao-desc {
  font-size: 1.125rem;
  color: #6b7280;
  line-height: 1.8;
  margin-bottom: 32px;
}

.cremacao-features {
  list-style: none;
  padding: 0;
  margin: 0 0 32px 0;
}

.cremacao-feature {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 24px;
}

.feat-icon {
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  background: #ecfdf5;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--primary);
}

.feat-icon svg {
  width: 24px;
  height: 24px;
}

.cremacao-feature h4 {
  font-size: 1rem;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 4px;
}

.cremacao-feature p {
  font-size: 0.875rem;
  color: #6b7280;
  line-height: 1.6;
}

.cremacao-images {
  display: grid;
  gap: 24px;
}

.cremacao-img {
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 10px 40px -10px rgba(52, 137, 77, 0.2);
}

.cremacao-img img {
  width: 100%;
  height: 256px;
  object-fit: cover;
}

/* Contact */
.contact-section {
  background: linear-gradient(135deg, var(--primary), var(--primary-dark));
  color: white;
}

.contact-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 24px;
  max-width: 64rem;
  margin: 0 auto;
}

.contact-card {
  display: block;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(8px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 16px;
  padding: 24px;
  text-align: center;
  text-decoration: none;
  color: inherit;
  transition: background 0.2s;
}

.contact-card:not(.no-link):hover {
  background: rgba(255, 255, 255, 0.15);
}

.contact-card.no-link {
  cursor: default;
}

.contact-icon {
  width: 32px;
  height: 32px;
  margin: 0 auto 12px;
  display: block;
}

.contact-card h4 {
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 8px;
}

.contact-card p {
  font-size: 0.875rem;
  opacity: 0.85;
  line-height: 1.5;
}

/* Responsive */
@media (max-width: 768px) {
  section {
    padding: 64px 0;
  }

  .featured-card {
    grid-template-columns: 1fr;
  }

  .featured-info {
    text-align: center;
  }

  .cremacao-content {
    grid-template-columns: 1fr;
    gap: 40px;
  }

  .cremacao-images {
    order: -1;
  }

  .contact-grid {
    grid-template-columns: 1fr 1fr;
  }
}

@media (max-width: 480px) {
  .hero-actions {
    flex-direction: column;
    align-items: stretch;
    width: 100%;
    max-width: 320px;
    margin-left: auto;
    margin-right: auto;
  }

  .btn-primary, .btn-secondary {
    width: 100%;
    justify-content: center;
    text-align: center;
  }

  .contact-grid {
    grid-template-columns: 1fr;
  }

  .floricultura-logo {
    height: 80px;
  }
}
</style>
